import { Flex } from 'antd';
import SuccessIcon from '/public/success.svg';
import ErrorIcon from '/public/error.svg';
import { IconWrapper } from '../ui/icon-wrapper';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { deviceService } from '@/services/device-service';
import { toast } from 'sonner';
import { waitForCache } from '@/lib/utils/promise-cache';
import { useUpdateEffect } from '@/hooks/utils/use-update-effect';
import { useNewStores } from '@/store/modules/agent-store';
import { useI18n } from '@/hooks/use-i18n';

export function DataSaverMsg({
  errorType,
  onContinue,
  platformName,
  platform,
}: {
  errorType: 'DEVICE_NOT_CONNECTED' | 'PLATFORM_NOT_LOGIN';
  onContinue: () => void;
  platformName?: string;
  platform?: string;
}) {
  const { deviceStore } = useNewStores();
  const { t } = useI18n(['thread', 'common']);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const Icon = isSuccess
    ? SuccessIcon
    : deviceStore.connectionStatus === 'connected:online'
      ? ErrorIcon
      : ErrorIcon;

  // 重新检测是否登录
  const handleReCheck = async (needToast = true) => {
    setIsChecking(true);
    try {
      const res = await deviceService.getDeviceStatus(
        deviceStore.selectedDeviceId,
        {
          platforms: [platform],
          detection: true,
        },
      );

      res.platforms.forEach((item) => {
        if (item.platform === platform) {
          setIsSuccess(item.logged_in);
          if (!item.logged_in && needToast) {
            toast.info(
              t('thread:message.dataSaver.platformNotLoggedIn', {
                platformName,
              }),
            );
          }
          return;
        }
      });
    } catch (error) {
      console.error(`重新检测平台失败: ${error}`);
      toast.error(t('thread:message.dataSaver.recheckFailed'));
    } finally {
      setIsChecking(false);
    }
  };

  const contentMessage = useMemo(() => {
    const isDeviceError = errorType === 'DEVICE_NOT_CONNECTED';
    if (isSuccess) {
      return isDeviceError
        ? t('thread:message.dataSaver.deviceConnectedSuccess')
        : t('thread:message.dataSaver.platformLoggedIn', { platformName });
    }
    return isDeviceError
      ? t('thread:message.dataSaver.deviceConnectionError')
      : t('thread:message.dataSaver.platformNotLoggedIn', { platformName });
  }, [errorType, platformName, isSuccess, t]);

  // 初始化检查：刚进来的时候就要查询一次，主要是处理从历史进来的时候用户必须得手动点一次的原因
  const initCheck = async () => {
    if (errorType === 'DEVICE_NOT_CONNECTED') {
      await deviceStore.flushDeviceInfo();
      setIsSuccess(deviceStore.connectionStatus === 'connected:online');
    } else {
      handleReCheck(false);
    }
  };

  useEffect(() => {
    initCheck();
  }, []);

  useUpdateEffect(() => {
    // 关闭连接Model显示的时候
    if (!deviceStore.isShowDeviceConnectionModel) {
      if (deviceStore.connectionStatus === 'connected:online') {
        setIsSuccess(true);
      }
    }
  }, [deviceStore.isShowDeviceConnectionModel]);

  return (
    <>
      <Flex
        align="center"
        style={{
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
        className={`w-full mt-[16px] h-[40px] rounded-[8px] bg-[${isSuccess ? '#7171EE33' : '#FF00001A'}] overflow-hidden`}
      >
        <IconWrapper
          offsetY={isSuccess ? 1 : 0}
          className={`mr-[4px] ${isSuccess ? '' : 'text-[#FF0000]'}`}
          icon={Icon}
          size={16}
        />
        <span
          className={`font-weight-[600] flex-1 text-[14px] text-[${isSuccess ? '#7171EE' : '#FF0000'}] truncate`}
        >
          {contentMessage}
        </span>

        {isSuccess ? (
          <span
            className="px-[8px] flex items-center justify-center border-[1px] h-[24px] border-[#7171EE] cursor-pointer  text-[13px] rounded-[999px] bg-[#fff] text-[#7171EE] hover:bg-[#7171EE] hover:text-[#fff] transition-colors duration-200"
            onClick={onContinue}
          >
            {t('common:continueExecution')}
          </span>
        ) : (
          <span
            className="cursor-pointer text-[12px] text-[#FF0000] hover:text-[#FF0000CC] transition-colors duration-200"
            onClick={() => {
              if (errorType === 'DEVICE_NOT_CONNECTED') {
                deviceStore.isShowDeviceConnectionModel = true;
              } else if (errorType === 'PLATFORM_NOT_LOGIN') {
                handleReCheck();
              }
            }}
          >
            {isChecking
              ? `${t('thread:message.dataSaver.recheck')}...`
              : errorType === 'DEVICE_NOT_CONNECTED'
                ? t('thread:message.dataSaver.recheck')
                : t('thread:message.dataSaver.recheck')}
          </span>
        )}
      </Flex>
    </>
  );
}
